'use client';

import { useState, useEffect } from 'react';

export interface FavoriteItem {
  id: string;
  slug: string;
  name: string;
  type: 'bikepark' | 'pumptrack' | 'snowpark' | 'trail';
  addedAt: string;
}

const STORAGE_KEY = 'bikeparkmapa-favorites';

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load favorites from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as FavoriteItem[];
        setFavorites(parsed);
      }
    } catch (error) {
      console.error('Failed to load favorites:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
      } catch (error) {
        console.error('Failed to save favorites:', error);
      }
    }
  }, [favorites, isLoaded]);

  const addFavorite = (item: Omit<FavoriteItem, 'addedAt'>) => {
    const newFavorite: FavoriteItem = {
      ...item,
      addedAt: new Date().toISOString(),
    };
    setFavorites((prev) => {
      // Prevent duplicates
      if (prev.some((fav) => fav.id === item.id && fav.type === item.type)) {
        return prev;
      }
      return [...prev, newFavorite];
    });
  };

  const removeFavorite = (id: string, type: FavoriteItem['type']) => {
    setFavorites((prev) => prev.filter((fav) => !(fav.id === id && fav.type === type)));
  };

  const isFavorite = (id: string, type: FavoriteItem['type']): boolean => {
    return favorites.some((fav) => fav.id === id && fav.type === type);
  };

  const toggleFavorite = (item: Omit<FavoriteItem, 'addedAt'>) => {
    if (isFavorite(item.id, item.type)) {
      removeFavorite(item.id, item.type);
    } else {
      addFavorite(item);
    }
  };

  const clearFavorites = () => {
    setFavorites([]);
  };

  const getFavoritesByType = (type: FavoriteItem['type']): FavoriteItem[] => {
    return favorites.filter((fav) => fav.type === type);
  };

  return {
    favorites,
    isLoaded,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite,
    clearFavorites,
    getFavoritesByType,
  };
}
